"use client";

import { SearchPanelContent } from "@/components/secondaryCarouselNavigationBar/secondaryCarouselNavigationToggleButton";
import { NAV_ACTION } from "@/utils/constants";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import type { RefObject } from "react";
import { useEffect, useLayoutEffect, useState } from "react";
import {
  findFragmentCategoryLink,
  readFragmentCategories,
} from "./fragmentCategories";

const OPEN_TOGGLE_CLASSES = ["outline", "outline-[#D5D5D5]"];

interface FragmentCategorySearchProps {
  containerRef: RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
}

interface PanelOffset {
  top: number;
  left: number;
}

function findToggle(container: HTMLElement | null): HTMLElement | null {
  return (
    container?.querySelector<HTMLElement>(
      `[data-wse-nav-action="${NAV_ACTION.CATEGORY_SEARCH}"]`
    ) ??
    container?.querySelector<HTMLElement>(
      pom.header.navigationHorizontalScrollToggleButton
    ) ??
    null
  );
}

function offsetBelow(toggle: HTMLElement, container: HTMLElement): PanelOffset {
  const toggleRect = toggle.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return {
    top: toggleRect.bottom - containerRect.top,
    left: toggleRect.left - containerRect.left,
  };
}

function scrollToCategory(container: HTMLElement, hash: string): void {
  const name = hash.slice(hash.indexOf("#") + 1);
  const section =
    document.getElementById(name) ??
    document.getElementById(name.replaceAll(/\s+/g, "-"));

  section?.scrollIntoView({ behavior: "smooth" });
  findFragmentCategoryLink(container, name)?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
    inline: "start",
  });
}

function useToggleOpenState(
  toggle: HTMLElement | null,
  isOpen: boolean
): void {
  useEffect(() => {
    if (!toggle || !isOpen) return;

    toggle.setAttribute("aria-expanded", "true");
    toggle.classList.add(...OPEN_TOGGLE_CLASSES);
    return () => {
      toggle.setAttribute("aria-expanded", "false");
      toggle.classList.remove(...OPEN_TOGGLE_CLASSES);
    };
  }, [toggle, isOpen]);
}

function useCloseOnOutsideMouseDown(
  isOpen: boolean,
  panel: HTMLElement | null,
  toggle: HTMLElement | null,
  onClose: () => void
): void {
  useEffect(() => {
    if (!isOpen) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panel?.contains(target) || toggle?.contains(target)) return;
      onClose();
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen, panel, toggle, onClose]);
}

export function FragmentCategorySearch({
  containerRef,
  isOpen,
  onClose,
}: Readonly<FragmentCategorySearchProps>) {
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [toggle, setToggle] = useState<HTMLElement | null>(null);
  const [offset, setOffset] = useState<PanelOffset | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const currentToggle = isOpen ? findToggle(container) : null;

    setToggle(currentToggle);
    setOffset(
      container && currentToggle
        ? offsetBelow(currentToggle, container)
        : null
    );
  }, [containerRef, isOpen]);

  const categories =
    isOpen && containerRef.current
      ? readFragmentCategories(containerRef.current)
      : [];

  useToggleOpenState(toggle, isOpen);
  useCloseOnOutsideMouseDown(isOpen, panel, toggle, onClose);

  if (!isOpen || !offset) return null;

  const closeAndRefocus = () => {
    onClose();
    toggle?.focus();
  };

  return (
    <div
      ref={setPanel}
      style={{ top: offset.top, left: offset.left }}
      className="absolute z-20 mt-3 w-screen sm:max-w-[350px] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 max-h-[500px] overflow-y-auto pb-8 pt-4"
      data-testid={applyDataTestId(pom.header.searchCategoriesPanel)}
    >
      <SearchPanelContent
        categories={categories}
        handleLinkClick={(hash) => {
          const container = containerRef.current;
          if (container) scrollToCategory(container, hash);
        }}
        close={closeAndRefocus}
      />
    </div>
  );
}